import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
  FaWhatsapp,
} from "react-icons/fa";
import aboutHero from "../assets/images/about-hero.jpg";
import Swal from "sweetalert2";

const Contact = () => {
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    mobile_number: "",
    message: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/create_contactUs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (res.ok) {
        setFormData({ fullname: "", email: "", mobile_number: "", message: "" });
        Swal.fire({
          icon: "success",
          title: "Message Sent!",
          text: data.message || "Your message was successfully submitted.",
          confirmButtonColor: "#8F8431",
          background: "#F9F9F6"
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: data.message || "Something went wrong.",
          confirmButtonColor: "#8F8431",
          background: "#F9F9F6"
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to submit form. Please try again later.",
        confirmButtonColor: "#8F8431",
        background: "#F9F9F6"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#EDE9E0] overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center bg-[#021437] overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: `url(${aboutHero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#021437]/70 to-[#031D47]/90" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 text-center px-4"
        >
          <h1 className="text-4xl md:text-6xl font-bold text-white tracking-tight">
            Contact Us
          </h1>
          <p className="text-[#C7C091] mt-3 text-lg md:text-xl">
            We're here to help you find your dream home
          </p>
        </motion.div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16">

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#EDE9E0] rounded-3xl p-8 md:p-10 shadow-[12px_12px_28px_#d4c9b8,-12px_-12px_28px_#ffffff] border border-[#E5E0D0]"
            >
              <h2 className="text-3xl font-bold text-[#2C3A2A] mb-8">Send us a Message</h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Full Name</label>
                    <input
                      type="text"
                      name="fullname"
                      value={formData.fullname}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                      className="w-full px-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      className="w-full px-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Phone Number</label>
                  <input
                    type="tel"
                    name="mobile_number"
                    value={formData.mobile_number}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    required
                    className="w-full px-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#3C4A3A] mb-2 ml-1">Message</label>
                  <textarea
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we help you today?"
                    required
                    className="w-full px-5 py-4 rounded-2xl bg-[#EDE9E0] shadow-[inset_6px_6px_12px_#d4c9b8,inset_-6px_-6px_12px_#ffffff] focus:shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] outline-none text-[#2C3A2A] resize-y"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-2xl font-semibold text-lg bg-[#8F8431] text-white shadow-[6px_6px_14px_#d4c9b8,-6px_-6px_14px_#ffffff] active:shadow-[inset_4px_4px_10px_#7A7130,inset_-4px_-4px_10px_#A89A5E] transition-all disabled:opacity-70"
                >
                  {loading ? "Sending Message..." : "Send Message"}
                </button>
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Contact Details Card */}
              <div className="bg-[#EDE9E0] rounded-3xl p-8 md:p-10 shadow-[12px_12px_28px_#d4c9b8,-12px_-12px_28px_#ffffff] border border-[#E5E0D0]">
                <h2 className="text-3xl font-bold text-[#2C3A2A] mb-8">Contact Information</h2>

                <div className="space-y-7">
                  <div className="flex gap-5">
                    <div className="w-12 h-12 rounded-2xl bg-[#8F8431] flex items-center justify-center flex-shrink-0 shadow-[4px_4px_10px_#d4c9b8,-4px_-4px_10px_#ffffff]">
                      <FaMapMarkerAlt className="text-white text-2xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C3A2A] mb-1">Office Address</h3>
                      <p className="text-[#385532] leading-relaxed">
                        Shop No. 6, Milan Plaza,<br />
                        Lal Bahadur Shastri Marg,<br />
                        Civil Lines, Prayagraj, Uttar Pradesh – 211001
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-5">
                    <div className="w-12 h-12 rounded-2xl bg-[#8F8431] flex items-center justify-center flex-shrink-0 shadow-[4px_4px_10px_#d4c9b8,-4px_-4px_10px_#ffffff]">
                      <FaPhoneAlt className="text-white text-2xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C3A2A] mb-1">Phone Numbers</h3>
                      <p className="text-[#385532]">+91 63866 46807</p>
                    </div>
                  </div>

                  <div className="flex gap-5">
                    <div className="w-12 h-12 rounded-2xl bg-[#8F8431] flex items-center justify-center flex-shrink-0 shadow-[4px_4px_10px_#d4c9b8,-4px_-4px_10px_#ffffff]">
                      <FaEnvelope className="text-white text-2xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C3A2A] mb-1">Email Addresses</h3>
                      <p className="text-[#385532] break-words">
                        dharaapariwar@gmail.com<br />
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-5">
                    <div className="w-12 h-12 rounded-2xl bg-[#8F8431] flex items-center justify-center flex-shrink-0 shadow-[4px_4px_10px_#d4c9b8,-4px_-4px_10px_#ffffff]">
                      <FaClock className="text-white text-2xl" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2C3A2A] mb-1">Working Hours</h3>
                      <p className="text-[#385532]">24/7 Service Available</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* WhatsApp Button */}
              <motion.a
                href="https://wa.me/+916386646807"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="flex items-center justify-center gap-3 bg-[#25D366] text-white py-4 rounded-3xl font-semibold text-lg shadow-[6px_6px_14px_#d4c9b8,-6px_-6px_14px_#ffffff] active:shadow-[inset_4px_4px_10px_#1EB05A,inset_-4px_-4px_10px_#4DFF8A]"
              >
                <FaWhatsapp className="text-3xl" />
                Chat with us on WhatsApp
              </motion.a>

              {/* Google Map */}
              <div className="bg-[#EDE9E0] rounded-3xl p-8 md:p-10 shadow-[12px_12px_28px_#d4c9b8,-12px_-12px_28px_#ffffff] border border-[#E5E0D0]">
                <h2 className="text-3xl font-bold text-[#2C3A2A] mb-6">Our Location</h2>
                <div className="rounded-2xl overflow-hidden shadow-inner border border-[#D4C9A8]">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1801.627566092389!2d81.76728193837714!3d25.429732516729665!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399acac5f8d8f8f%3A0x1234567890abcdef!2sShop%20No.%206%2C%20Milan%20Plaza%2C%20Civil%20Lines%2C%20Prayagraj!5e0!3m2!1sen!2sin!4v1743835419597!5m2!1sen!2sin"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;